import clsx from "clsx"
import React, { forwardRef, memo, useRef } from "react"
import { createPortal } from "react-dom"
import styled from "styled-components"
import cn from "clsx"
import Heading, { HeadingProps } from "components/Typography/Heading"
import BodyText from "components/Typography/BodyText"
import CloseIcon from "../icons/CloseIcon"
import DownloadIcon from "../icons/DownloadIcon"
import BackToTopButton from "components/BackToTopButton/BackToTopButton"

export const ModalContent = styled.div`
  flex-direction: column;
  border-radius: 20px;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0);
  z-index: 1000;
  padding: 24px;
  gap: 24px;

  min-height: 200px;
  min-width: 200px;
  max-height: 90vh;

  overflow-y: auto;
  overflow-x: hidden;

  display: flex;

  background: #fff;

  &::-webkit-scrollbar {
    width: 6px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 10px;
    margin-top: 18px;
    margin-bottom: 18px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.palette.gray_light};
    border-radius: 10px;
    cursor: pointer;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${(props) => props.theme.palette.gray_medium};
  }

  &.openModal {
    transform: translate(-50%, -50%) scale(1);
  }

  &.size-sm {
    width: 400px;
  }

  &.size-md {
    width: 640px;
  }

  &.size-1-lg {
    width: 800px;
  }

  &.size-2-lg {
    width: 980px;
  }

  &.size-3-lg {
    width: 1128px;
  }

  &.height-md {
    max-height: 760px;
  }
`

export const ButtonClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
`

const Overlay = styled.div`
  &.overlay {
    cursor: not-allowed;
    position: fixed;
    display: flex;
    justify-content: center;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(23, 20, 20, 0.3);
    z-index: 999;
    overflow: hidden;
    justify-content: center;
    align-items: center;
  }
`

const TitleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ButtonDownload = styled.button`
  display: flex;
  align-items: center;
  justify-content: end;
  position: absolute;
  top: 16px;
  right: 84px;
  gap: 4px;
`

const ModalDiv = styled.div``

interface ModalProps {
  children?: React.ReactElement
  className?: string
  openModal: boolean
  closeModal?: () => void
  title?: string
  size?: "sm" | "md" | "1-lg" | "2-lg" | "3-lg"
  height?: "md" | ""
  download?: () => void
  src?: string
  color?: HeadingProps["color"]
  testId?: string
  backToTop?: boolean
}

const ModalComponent: React.FC<ModalProps> = forwardRef<HTMLDivElement, ModalProps>((props, ref) => {
  const {
    children,
    className,
    openModal,
    closeModal,
    title,
    size,
    download,
    src,
    color,
    testId,
    height = "",
    backToTop,
    ...rest
  } = props
  const containerRef = useRef<HTMLDivElement>(null)

  return (
    <ModalDiv {...rest} ref={ref} className={className} data-testid={testId}>
      <Overlay className={clsx({ overlay: openModal })}>
        {createPortal(
          <ModalContent
            ref={containerRef}
            className={clsx({ openModal: openModal }, "shelf-modal-body", cn(`size-${size}`), cn(`height-${height}`))}
          >
            {closeModal && (
              <ButtonClose onClick={closeModal}>
                <CloseIcon />
              </ButtonClose>
            )}
            {title && (
              <TitleHeader>
                {src && <img src={src} />}
                <Heading variant="h4" color={color}>
                  {title}
                </Heading>
              </TitleHeader>
            )}
            {download && (
              <ButtonDownload onClick={download}>
                <DownloadIcon />
                <BodyText size="small" weight="semi-bold" color="primary-dark">
                  Download
                </BodyText>
              </ButtonDownload>
            )}
            {children}
            {backToTop && <BackToTopButton scrollContainer={containerRef} />}
          </ModalContent>,
          document.getElementById("portal") as HTMLElement
        )}
      </Overlay>
    </ModalDiv>
  )
})

export default memo(ModalComponent)
